import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    Squares2X2Icon,
    ChevronDoubleRightIcon,
    ArrowRightOnRectangleIcon,
    ChevronDoubleLeftIcon,
    BuildingOffice2Icon,
    UsersIcon,
    ClipboardDocumentListIcon,
    DocumentTextIcon,
    BanknotesIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { clearAccessToken, getCurrentUser } from "../../../utils/auth/tokenStore";
import { useLoading } from "../../../components/globalloaders/LoadingContext";
import ConfirmDialog from "../../../components/dialogbox/ConfirmDialog";

const Sidebar = ({ isMobileOpen, setIsMobileOpen }) => {

    const { isLoading, setIsLoading } = useLoading();
    const [dialog, setDialog] = useState({
        open: false,
        type: "info",
        title: "",
        message: "",
        confirmText: "",
        action: () => { },
    });
    const navigate = useNavigate();
    const user = getCurrentUser();

    const [isCollapsed, setIsCollapsed] = useState(true);
    const location = useLocation();

    // Handle viewport resize events
    useEffect(() => {
        const handleResize = () => {
            const isDesktop = window.innerWidth >= 768;
            if (isDesktop) {
                setIsMobileOpen(false); // Close mobile menu on desktop
            } else {
                setIsCollapsed(false); // Ensure full width on mobile
                setIsMobileOpen(false); // Close sidebar when resizing to mobile
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Initialize state based on initial viewport

        return () => window.removeEventListener('resize', handleResize);
    }, [setIsMobileOpen]);

    const menuItems = [
        { name: "Dashboard", icon: Squares2X2Icon, route: "/admin" },
        { name: "Client", icon: BuildingOffice2Icon, route: "/admin/client" },
        { name: "Employee", icon: UsersIcon, route: "/admin/employee" },
        { name: "Entry", icon: ClipboardDocumentListIcon, route: "/admin/entry" },
        { name: "Agreement", icon: DocumentTextIcon, route: "/admin/agreement" },
        { name: "Finance", icon: BanknotesIcon, route: "/admin/finance" },
    ];

    // Logout API
    const handleLogout = async () => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const res = await axios.post(`${process.env.REACT_APP_URI}/auth/logout`, {}, { withCredentials: true });
            if (res.data?.success) {
                clearAccessToken();
                navigate("/", { replace: true });
                toast.success(res.data.message);
            } else {
                toast.error(res.data.message);
            }
        } catch (err) {
            console.log("Logout failed:", err);
            toast.error(err?.response?.data?.message || "Network Error while logout!")
        } finally {
            setIsLoading(false);
            return;
        }
    };

    return (
        <>
            <ConfirmDialog
                isOpen={dialog.open}
                onClose={() => setDialog((d) => ({ ...d, open: false }))}
                onConfirm={dialog.action}
                title={dialog.title}
                message={dialog.message}
                confirmText={dialog.confirmText}
                type={dialog.type}
            />
            {/* Sidebar */}
            <div className={` transition-all duration-300 bg-white shadow-xl flex flex-col z-40
            ${isMobileOpen
                    ? "fixed inset-0 w-64"
                    : `${isCollapsed ? "w-20" : "w-64"} hidden md:flex sticky top-0 h-screen`}
            `} >
                {/* Header Section */}
                <div className="flex justify-between p-6 border-b flex-shrink-0">
                    <div className={`flex items-center ${isCollapsed ? "justify-center" : "space-x-3"}`}>
                        <div className={`flex items-center ${isCollapsed ? "justify-center" : "space-x-2"} transition-all duration-300 select-none`} >
                            {/* === Symbol === */}
                            <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 text-white font-bold text-sm">
                                WS
                            </div>
                            {/* === Brand Text === */}
                            {!isCollapsed && (
                                <span className="text-lg sm:text-xl font-bold tracking-tight bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 bg-clip-text text-transparent">
                                    MBEO Workspace
                                </span>
                            )}
                        </div>
                    </div>
                    {/* Close Button for Mobile */}
                    <button className="flex items-start mb-5 md:hidden text-gray-600 hover:text-gray-900" onClick={() => setIsMobileOpen(false)}>
                        ✖
                    </button>
                </div>
                {/* Scrollable Content Area of Navbar */}
                <div className="flex-1 overflow-y-auto">
                    <nav className="flex flex-col p-4 space-y-[14px]">
                        {menuItems.map((item) => (
                            <Link
                                key={item.name}
                                to={item.route}
                                className={`group flex items-center rounded-lg p-3 transition-all 
                                ${location.pathname === item.route ? "bg-indigo-50 text-indigo-600 border-l-4 border-indigo-600" : "text-gray-600 hover:bg-gray-50"}
                                ${isCollapsed ? "justify-center" : "space-x-3"}`}
                                onClick={() => setIsMobileOpen(false)} // Close sidebar on link click
                            >
                                <item.icon className="h-5 w-5 flex-shrink-0 min-w-[20px]" />
                                {!isCollapsed && <span className="text-sm font-medium truncate">{item.name}</span>}
                            </Link>
                        ))}
                    </nav>
                </div>
                {/* Profile Section with Collapse Button & Logout */}
                <div className={`sticky bottom-0 border-t bg-white ${isCollapsed ? "px-2" : "px-4"}`}>
                    <div className={`flex items-center ${isCollapsed ? "justify-center py-4" : "justify-between py-2"}`}>
                        {!isCollapsed ? (
                            <>
                                <Link
                                    to="/admin/account"
                                    onClick={() => setIsMobileOpen(false)}
                                    className={`group flex items-center w-full max-w-full gap-2 rounded-lg p-2 transition-all duration-300 cursor-pointer overflow-hidden
                                        ${location.pathname === "/admin/account" ? "bg-indigo-50 border-l-4 border-indigo-600" : "hover:bg-indigo-50/70"}
                                        `}
                                >
                                    {/* Avatar */}
                                    <div className="relative flex-shrink-0">
                                        <div className="h-9 w-9 bg-gradient-to-br from-indigo-600 to-indigo-500 rounded-lg flex items-center justify-center text-white font-semibold shadow-sm">
                                            AD
                                        </div>
                                        <div className="absolute bottom-0 right-0 h-2.5 w-2.5 bg-green-500 border-2 border-white rounded-full"></div>
                                    </div>
                                    {/* User Info */}
                                    <div className="min-w-0 flex-1 flex flex-col overflow-hidden">
                                        <p className="text-sm font-semibold text-gray-800 truncate group-hover:text-indigo-700 transition-colors">
                                            Admin
                                        </p>
                                        <p className="text-xs text-gray-500 truncate group-hover:text-gray-600 transition-colors">
                                            {user?.username || "admin@mbeows.com"}
                                        </p>
                                    </div>
                                </Link>
                                {/* Hide the collapse button when isMobileOpen is true */}
                                {!isMobileOpen && (
                                    <button
                                        onClick={() => setIsCollapsed(!isCollapsed)}
                                        className="ml-1 p-1 hover:bg-gray-100 rounded-lg"
                                    >
                                        <ChevronDoubleLeftIcon className="h-5 w-5" />
                                    </button>
                                )}
                            </>
                        ) : (
                            !isMobileOpen && ( // Hide when mobile menu is open
                                <button
                                    onClick={() => setIsCollapsed(!isCollapsed)}
                                    className="p-1 hover:bg-gray-100 rounded-lg"
                                >
                                    <ChevronDoubleRightIcon className="h-5 w-5" />
                                </button>
                            )
                        )}
                    </div>
                    <div className="flex justify-center pb-1">
                        <button
                            onClick={() => {
                                setDialog({
                                    open: true,
                                    type: "warning",
                                    title: "Logout Confirmation",
                                    message: "Are you sure you want to logout of the Admin panel?",
                                    confirmText: "Logout",
                                    action: async () => {
                                        setDialog(d => ({ ...d, open: false }));
                                        await handleLogout();
                                    }
                                });
                            }}
                            className="flex items-center gap-2 rounded-lg px-4 py-2 text-red-600 hover:bg-red-50 transition-all"
                        >
                            <ArrowRightOnRectangleIcon className="h-5 w-5 flex-shrink-0" />
                            {!isCollapsed && (
                                <span className="text-sm font-medium">Logout</span>
                            )}
                        </button>
                    </div>
                    <div className="flex justify-center pb-2">
                        {!isCollapsed && (
                            <span className="text-[12px] text-gray-500 tracking-wide">
                                v{process.env.REACT_APP_VERSION || "1.0.0"}
                            </span>
                        )}
                    </div>
                </div>
            </div>
            {/* Mobile Overlay */}
            {isMobileOpen && (
                <div
                    className="fixed inset-0 bg-black opacity-50 md:hidden z-30"
                    onClick={() => setIsMobileOpen(false)}
                />
            )}
        </>
    );
};

export default Sidebar;